import { useCallback, useMemo } from 'react'
import { Table, Thead, Tbody, Tr, Td, Th, TableScrollHost } from '@/common/ui/Table'
import { Card } from '@/common/ui/Card/Card'
import { Button } from '@/common/ui/Form/Button/Button'
import { cn } from '@/common/lib/cn/cn'
import type { GenerationCriteriaAvgSegmentScoreSchema, SimpleCriteriaScoreSchema } from '@/common/api/client/types.gen'
import { useCompanyStatsSegmentScores } from '../model'
import type { CompanyStatsDataHooks } from '../model/hooks'
import { exportRowsToExcel } from '../lib/exportExcel'
import { DASHBOARD_BLOCK_TYPES, getSegmentPresenceTier, productScorePillClass } from '../lib/scoreColors'
import { getCriteriaLabel } from '../lib/labels'
import { CompanyStatsExcelExportButton } from './CompanyStatsExcelExportButton'

const DIFF_ALERT_THRESHOLD = 30

const SEGMENT_LEGEND = [
    { tier: 'success', label: '≥75% наличие', swatchClass: 'bg-[#08AC81]' },
    { tier: 'warning', label: '40–74%', swatchClass: 'bg-[#F59E0B]' },
    { tier: 'error', label: '<40%', swatchClass: 'bg-[#FF484B]' },
] as const

type SegmentColumn = {
    key: 'best_score' | 'middle_score' | 'low_score'
    label: string
    count: number
}

type CompanyStatsSegmentComparisonProps = {
    orgId: string
    brandId?: string
    sellerId?: string
    hooks?: Pick<CompanyStatsDataHooks, 'useCompanyStatsSegmentScores'>
}

const formatPercent = (score: SimpleCriteriaScoreSchema | null | undefined) =>
    score?.percent != null ? Math.round(score.percent) : null

const SegmentPercentPill = ({ percent, label }: { percent: number | null; label: string }) => {
    if (percent == null) {
        return (
            <span className='text-base-content/40' aria-hidden>
                —
            </span>
        )
    }

    return (
        <span
            className={cn(
                'inline-flex items-center justify-center rounded px-2 py-0.5 text-xs font-medium tabular-nums',
                productScorePillClass[getSegmentPresenceTier(percent)]
            )}
            aria-label={`${label}: ${percent}%`}
        >
            {percent}%
        </span>
    )
}

export const CompanyStatsSegmentComparison = ({
    orgId,
    brandId,
    sellerId,
    hooks = { useCompanyStatsSegmentScores },
}: CompanyStatsSegmentComparisonProps) => {
    const { data, isLoading, error, refetch } = hooks.useCompanyStatsSegmentScores(orgId, {
        brand: brandId,
        seller: sellerId,
    })

    const criterias = useMemo<GenerationCriteriaAvgSegmentScoreSchema[]>(() => {
        const blocks = (data?.blocks ?? []).filter(block => DASHBOARD_BLOCK_TYPES.includes(block.block_type))
        return blocks.flatMap(block => block.criterias ?? [])
    }, [data?.blocks])

    const segmentColumns = useMemo<SegmentColumn[]>(
        () => [
            { key: 'best_score', label: 'ТОП (8+)', count: data?.best_count ?? 0 },
            { key: 'middle_score', label: 'Средние (6–7)', count: data?.middle_count ?? 0 },
            { key: 'low_score', label: 'Слабые (≤5)', count: data?.low_count ?? 0 },
        ],
        [data?.best_count, data?.middle_count, data?.low_count]
    )

    const handleExport = useCallback(() => {
        if (criterias.length === 0) return
        exportRowsToExcel(
            criterias,
            [
                {
                    header: 'Критерий',
                    getValue: row => getCriteriaLabel(row.title, row.title_type, row.criteria_name),
                },
                ...segmentColumns.map(col => ({
                    header: `${col.label} (${col.count} тов.)`,
                    getValue: (row: GenerationCriteriaAvgSegmentScoreSchema) => {
                        const percent = formatPercent(row[col.key])
                        return percent != null ? `${percent}%` : '—'
                    },
                })),
                {
                    header: 'Разрыв',
                    getValue: row => {
                        const diffPercent = formatPercent(row.diff_score)
                        return diffPercent != null ? `${diffPercent}%` : '—'
                    },
                },
            ],
            'company_stats_segments',
            'Сегменты'
        )
    }, [criterias, segmentColumns])

    if (error) {
        return (
            <Card color='primary'>
                <div className='flex flex-col gap-4'>
                    <h3 className='text-xs font-bold uppercase tracking-wide text-white'>Сравнение сегментов</h3>
                    <div className='alert alert-error'>
                        <span>Ошибка загрузки: {error.message}</span>
                    </div>
                    <Button type='button' emphasis='accent' onClick={() => void refetch()}>
                        Попробовать снова
                    </Button>
                </div>
            </Card>
        )
    }

    if (!isLoading && criterias.length === 0) {
        return null
    }

    return (
        <Card color='primary' tableScroll>
            <div className='mb-[1rem] flex shrink-0 flex-wrap items-center justify-between gap-2'>
                <div className='flex flex-wrap items-baseline gap-2'>
                    <h3 className='text-xs font-bold uppercase tracking-wide text-white'>Сравнение сегментов</h3>
                    <span className='text-xs text-base-content/50'>— товары разбиты по итоговому баллу</span>
                </div>
                <CompanyStatsExcelExportButton
                    onClick={handleExport}
                    disabled={criterias.length === 0}
                    ariaLabel='Экспорт сравнения сегментов в Excel'
                />
            </div>

            <TableScrollHost minHeight='10rem'>
                <Table pinRows striped thBg rounded={false} className='w-full min-w-[40rem]'>
                    <Thead>
                        <Tr>
                            {[
                                <Th key='criteria'>Критерий</Th>,
                                ...segmentColumns.map(col => (
                                    <Th key={col.key} className='text-center'>
                                        <span className='flex flex-col items-center gap-0.5'>
                                            <span>{col.label}</span>
                                            <span className='font-normal text-base-content/50 normal-case'>
                                                {col.count} тов.
                                            </span>
                                        </span>
                                    </Th>
                                )),
                                <Th key='diff' className='text-center'>
                                    Разрыв
                                </Th>,
                            ]}
                        </Tr>
                    </Thead>
                    <Tbody>
                        {isLoading && criterias.length === 0 ? (
                            <Tr>
                                <Td colSpan={5} className='text-center py-8 text-base-content/70'>
                                    <span className='loading loading-spinner loading-md' aria-hidden />
                                </Td>
                            </Tr>
                        ) : (
                            criterias.map(criteria => {
                                const label = getCriteriaLabel(
                                    criteria.title,
                                    criteria.title_type,
                                    criteria.criteria_name
                                )
                                const diffPercent = formatPercent(criteria.diff_score)

                                return (
                                    <Tr key={criteria.criteria_name}>
                                        {[
                                            <Td key='criteria' className='text-sm text-base-content/80'>
                                                {label}
                                            </Td>,
                                            ...segmentColumns.map(col => (
                                                <Td key={col.key} className='text-center'>
                                                    <SegmentPercentPill
                                                        percent={formatPercent(criteria[col.key])}
                                                        label={`${label}, ${col.label}`}
                                                    />
                                                </Td>
                                            )),
                                            <Td
                                                key='diff'
                                                className={cn(
                                                    'text-center text-xs font-medium tabular-nums',
                                                    diffPercent != null && diffPercent >= DIFF_ALERT_THRESHOLD
                                                        ? 'text-[#FF484B]'
                                                        : 'text-base-content/70'
                                                )}
                                            >
                                                {diffPercent != null ? `${diffPercent}%` : '—'}
                                            </Td>,
                                        ]}
                                    </Tr>
                                )
                            })
                        )}
                    </Tbody>
                </Table>
            </TableScrollHost>

            <ul className='flex flex-wrap items-center gap-4 mt-4' aria-label='Легенда цветов сегментов'>
                {SEGMENT_LEGEND.map(item => (
                    <li key={item.tier} className='flex items-center gap-1.5 text-xs text-base-content/60'>
                        <span className={cn('inline-block w-2.5 h-2.5 rounded-sm', item.swatchClass)} aria-hidden />
                        {item.label}
                    </li>
                ))}
            </ul>
        </Card>
    )
}
